import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getClaim } from "../../api/getClaim";
import { getOrderLookup } from "../../api/orderLookup";
import { useTenantTheme } from "../../themes/TenantThemeProvider";
import type { Claim } from "./types";

export const useClaim = (claimNumber: string | undefined) => {
  const { setIsAppLoading } = useTenantTheme();
  const [claim, setClaim] = useState<Claim | undefined>();

  useEffect(() => {
    if (!claimNumber) {
      return undefined;
    }

    let cancelled = false;
    setIsAppLoading(true);

    const fetchClaim = async () => {
      try {
        const response = await getClaim(claimNumber);
        if (!cancelled && response.data) {
          setClaim(response.data);
        }
      } finally {
        if (!cancelled) {
          setIsAppLoading(false);
        }
      }
    };

    fetchClaim();

    return () => {
      cancelled = true;
    };
  }, [claimNumber, setIsAppLoading]);

  return { claim };
};

export const usePreFetchOrder = (onOrderLookupSuccess: (data: unknown) => void) => {
  const { setIsAppLoading } = useTenantTheme();
  const [hasFetchedOrder, setHasFetchedOrder] = useState(false);
  const [searchParams] = useSearchParams();
  const shipInsureIdParam = searchParams.get("ship_insure_id");

  useEffect(() => {
    if (!hasFetchedOrder && shipInsureIdParam) {
      setIsAppLoading(true);
      prepopulateOrder(shipInsureIdParam);
    }

    async function prepopulateOrder(shipInsureId: string) {
      try {
        const response = await getOrderLookup({ shipInsureId });
        onOrderLookupSuccess(response.data);
      } catch {
        // silently fail
      } finally {
        setHasFetchedOrder(true);
        setIsAppLoading(false);
      }
    }
  }, [
    hasFetchedOrder,
    onOrderLookupSuccess,
    setIsAppLoading,
    shipInsureIdParam,
  ]);
};

export const useScreenAnalytics = (activeStep: number) => {
  useEffect(() => {
    const stepMap = {
      0: "Order Lookup",
      1: "Claim Details",
      2: "Claim Review",
      3: "Submitted",
    };
    void stepMap[activeStep as keyof typeof stepMap];
  }, [activeStep]);
};
